/**
 * Generated by orval v6.31.0 🍺
 * Do not edit manually.
 * Report Server API
 * Report Server API - 신고 관리 시스템

## 주요 기능
- 신고 생성 및 조회
- 신고 검토 및 처리 (관리자)
- 신고 통계 분석
- 우선순위 관리
- 증거 자료 관리
- 다양한 타입 지원 (코스, 사용자, 댓글, 게시글 등)

## 아키텍처
- Spring Data JPA (PostgreSQL)
- OAuth2 JWT 기반 인증
- 관리자 권한 기반 검토 시스템
- AI 모더레이션 점수 지원

## 신고 처리 플로우
1. PENDING (접수됨) → 신고 생성
2. REVIEWING (검토 중) → 관리자 검토 시작
3. RESOLVED (처리 완료) 또는 REJECTED (기각됨) → 최종 결정
4. APPEALED (항소됨) → 재검토 요청
5. CLOSED (종료됨) → 완전 종료

 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  AddEvidenceRequest,
  CreateReportRequest,
  CreateReportResponse,
  ErrorResponse,
  GetReportsByReporterParams,
  GetReportsByTargetParams,
  Report,
  ReportTargetType,
  UpdateReportResponse,
} from "./reportServerAPI.schemas";
import { customInstance } from "../custom-axios";
import type { ErrorType } from "../custom-axios";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * 새로운 신고를 생성합니다. 코스, 사용자, 댓글, 게시글 등 다양한 타입의 대상을 신고할 수 있습니다.
- 증거 자료 URL을 여러 개 첨부 가능
- 메타데이터를 통해 추가 정보 전달 가능
- IP 주소와 User-Agent가 자동으로 기록됨

 * @summary 신고 생성
 */
export const createReport = (
  createReportRequest: CreateReportRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<CreateReportResponse>(
    {
      url: `/api/reports`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: createReportRequest,
    },
    options,
  );
};

export const getCreateReportMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createReport>>,
    TError,
    { data: CreateReportRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createReport>>,
  TError,
  { data: CreateReportRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createReport>>,
    { data: CreateReportRequest }
  > = (props) => {
    const { data } = props ?? {};

    return createReport(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateReportMutationResult = NonNullable<
  Awaited<ReturnType<typeof createReport>>
>;
export type CreateReportMutationBody = CreateReportRequest;
export type CreateReportMutationError = ErrorType<ErrorResponse>;

/**
 * @summary 신고 생성
 */
export const useCreateReport = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createReport>>,
    TError,
    { data: CreateReportRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createReport>>,
  TError,
  { data: CreateReportRequest },
  TContext
> => {
  const mutationOptions = getCreateReportMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * 특정 신고의 상세 정보를 조회합니다.
 * @summary 신고 상세 조회
 */
export const getReportById = (
  id: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<Report>(
    { url: `/api/reports/${id}`, method: "GET", signal },
    options,
  );
};

export const getGetReportByIdQueryKey = (id: string) => {
  return [`/api/reports/${id}`] as const;
};

export const getGetReportByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getReportById>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReportById>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReportByIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReportById>>> = ({
    signal,
  }) => getReportById(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getReportById>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetReportByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReportById>>
>;
export type GetReportByIdQueryError = ErrorType<ErrorResponse>;

/**
 * @summary 신고 상세 조회
 */
export const useGetReportById = <
  TData = Awaited<ReturnType<typeof getReportById>>,
  TError = ErrorType<ErrorResponse>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getReportById>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetReportByIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * 특정 대상(코스, 사용자 등)에 대한 모든 신고를 조회합니다.
 * @summary 대상별 신고 목록 조회
 */
export const getReportsByTarget = (
  targetType: ReportTargetType,
  targetId: string,
  params?: GetReportsByTargetParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<Report[]>(
    {
      url: `/api/reports/target/${targetType}/${targetId}`,
      method: "GET",
      params,
      signal,
    },
    options,
  );
};

export const getGetReportsByTargetQueryKey = (
  targetType: ReportTargetType,
  targetId: string,
  params?: GetReportsByTargetParams,
) => {
  return [
    `/api/reports/target/${targetType}/${targetId}`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetReportsByTargetQueryOptions = <
  TData = Awaited<ReturnType<typeof getReportsByTarget>>,
  TError = ErrorType<ErrorResponse>,
>(
  targetType: ReportTargetType,
  targetId: string,
  params?: GetReportsByTargetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReportsByTarget>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetReportsByTargetQueryKey(targetType, targetId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReportsByTarget>>
  > = ({ signal }) =>
    getReportsByTarget(targetType, targetId, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(targetType && targetId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getReportsByTarget>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetReportsByTargetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReportsByTarget>>
>;
export type GetReportsByTargetQueryError = ErrorType<ErrorResponse>;

/**
 * @summary 대상별 신고 목록 조회
 */
export const useGetReportsByTarget = <
  TData = Awaited<ReturnType<typeof getReportsByTarget>>,
  TError = ErrorType<ErrorResponse>,
>(
  targetType: ReportTargetType,
  targetId: string,
  params?: GetReportsByTargetParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReportsByTarget>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetReportsByTargetQueryOptions(
    targetType,
    targetId,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * 특정 사용자가 제출한 모든 신고를 조회합니다.
 * @summary 신고자별 신고 목록 조회
 */
export const getReportsByReporter = (
  reporterId: string,
  params?: GetReportsByReporterParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<Report[]>(
    {
      url: `/api/reports/reporter/${reporterId}`,
      method: "GET",
      params,
      signal,
    },
    options,
  );
};

export const getGetReportsByReporterQueryKey = (
  reporterId: string,
  params?: GetReportsByReporterParams,
) => {
  return [
    `/api/reports/reporter/${reporterId}`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetReportsByReporterQueryOptions = <
  TData = Awaited<ReturnType<typeof getReportsByReporter>>,
  TError = ErrorType<ErrorResponse>,
>(
  reporterId: string,
  params?: GetReportsByReporterParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReportsByReporter>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetReportsByReporterQueryKey(reporterId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReportsByReporter>>
  > = ({ signal }) =>
    getReportsByReporter(reporterId, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!reporterId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getReportsByReporter>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetReportsByReporterQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReportsByReporter>>
>;
export type GetReportsByReporterQueryError = ErrorType<ErrorResponse>;

/**
 * @summary 신고자별 신고 목록 조회
 */
export const useGetReportsByReporter = <
  TData = Awaited<ReturnType<typeof getReportsByReporter>>,
  TError = ErrorType<ErrorResponse>,
>(
  reporterId: string,
  params?: GetReportsByReporterParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReportsByReporter>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetReportsByReporterQueryOptions(
    reporterId,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * 기존 신고에 추가 증거 자료를 첨부합니다.
신고자 또는 관리자가 추가 증거를 제출할 때 사용합니다.

 * @summary 증거 추가
 */
export const addEvidence = (
  id: string,
  addEvidenceRequest: AddEvidenceRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<UpdateReportResponse>(
    {
      url: `/api/reports/${id}/evidence`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: addEvidenceRequest,
    },
    options,
  );
};

export const getAddEvidenceMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addEvidence>>,
    TError,
    { id: string; data: AddEvidenceRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addEvidence>>,
  TError,
  { id: string; data: AddEvidenceRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addEvidence>>,
    { id: string; data: AddEvidenceRequest }
  > = (props) => {
    const { id, data } = props ?? {};

    return addEvidence(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddEvidenceMutationResult = NonNullable<
  Awaited<ReturnType<typeof addEvidence>>
>;
export type AddEvidenceMutationBody = AddEvidenceRequest;
export type AddEvidenceMutationError = ErrorType<ErrorResponse>;

/**
 * @summary 증거 추가
 */
export const useAddEvidence = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addEvidence>>,
    TError,
    { id: string; data: AddEvidenceRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof addEvidence>>,
  TError,
  { id: string; data: AddEvidenceRequest },
  TContext
> => {
  const mutationOptions = getAddEvidenceMutationOptions(options);

  return useMutation(mutationOptions);
};
